import { Button, Dropdown, Spin, Tag, Modal, Avatar } from "antd";
import { useState } from "react";
import InfiniteTable from "../utils/InfiniteTable";
import {
  DeleteFilled,
  DownOutlined,
  EditFilled,
  EyeFilled,
  LockFilled,
  UnlockFilled,
  UserAddOutlined,
} from "@ant-design/icons";
import { useUserContext } from "../../contexts/UserContext";
import Toast from "../../helpers/Toast";
import { catchError } from "../../services/DaoService";
import { PersonnelService } from "../../services/PersonnelService";
import { NavLink, useNavigate } from "react-router-dom";
import PersonnelEditForm from "./PersonnelEditForm";
import PropTypes from "prop-types";
import UserService from "../../services/UserService";

const PersonnelList = ({ paginationData, setPaginationData, personnels, setPersonnels, searchParams }) => {
  const { check } = useUserContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);

  const loadMore = () => {
    if (paginationData?.next_page_url && !loadingMore) {
      setLoadingMore(true);
      PersonnelService
        .post(paginationData.next_page_url, searchParams)
        .then((response) => {
          if (!response) {
            return;
          }
          setPaginationData(response.data);
          setPersonnels(personnels => [...personnels, ...response.data.data]);
        })
        .catch((error) => {
          catchError(error);
        })
        .finally(() => {
          setLoadingMore(false);
        });
    }
  };
  const handleDelete = (idOrUid) => {
    setLoading(true);
    PersonnelService.getInstance()
      .remove(idOrUid)
      .then((response) => {
        Toast.success(response.message);
        setPersonnels((items) =>
          items.filter((item) => item.uid !== idOrUid)
        );
      })
      .catch((error) => {
        catchError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openEditModal = (item) => {
    setSelectedPersonnel(item);
    setUpdateModalVisible(true);
  };

  const handleEnable = (personnel) => {
    PersonnelService.getInstance()
      .enable(personnel.uid)
      .then((response) => {
        Toast.success(response.message);
        setPersonnels(
          (personnels) => personnels.map((u) =>
            u.uid === response.data.uid ? response.data : u
          )
        );
      })
      .catch((error) => {
        Toast.error(error.message);
      });
  };

  const handleDisable = (personnel) => {
    PersonnelService.getInstance()
      .disable(personnel.uid)
      .then((response) => {
        Toast.success(response.message);
        setPersonnels(
          personnels => personnels.map((u) =>
            u.uid === response.data.uid ? response.data : u
          )
        );
      })
      .catch((error) => {
        Toast.error(error.message);
      });
  };

  const handleUpdate = (item) => {
    setPersonnels(
      personnels => personnels.map((u) => (u.uid === item.uid ? item : u))
    );
    setSelectedPersonnel(null);
    setUpdateModalVisible(false);
  };

  const gotoPersonnelDetails = (item) => {
    navigate(`/personnel/${item.uid}`);
  };

  const generateAccount = (personnel) => {
    setLoading(true);
    const user = { ...personnel, personnel_id: personnel.id };
    UserService.getInstance()
      .create(user)
      .then((response) => {
        Toast.success(response.message);
        setPersonnels(
          (personnels) => personnels.map((u) =>
            u.uid === response.data.uid ? response.data : u
          )
        );
      })
      .catch((error) => {
        catchError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  let columns = [
    {
      title: "Photo",
      dataIndex: "avatar_link",
      key: "avatar",
      width: 80,
      render: (avatar, record) => check('VIEW-PERSONNEL') ?
        <NavLink to={`/personnel/${record.uid}`}>
          <Avatar size={48} src={avatar} />
        </NavLink> :
        <Avatar size={48} src={avatar} className="cursor-not-allowed" />
    },
    {
      title: "Prénoms",
      dataIndex: "prenoms",
      key: "prenoms",
      width: 200,
    },
    {
      title: "Nom",
      dataIndex: "nom",
      key: "nom",
      width: 150,
      render : (nom, record) => (
        <>
          {check('VIEW-PERSONNEL') && nom ?
            (<NavLink to={`/personnel/${record.uid}`}>
              {nom}
            </NavLink>
            ) : nom
          }
        </>
      )
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 350,
      render : (email) => (
        <a href={`mailto:${email}`}>{email}</a>
      )
    },
    {
      title: "Téléphone",
      dataIndex: "telephone",
      key: "telephone",
      width: 150,
      render : (telephone) => (
        <a href={`tel:${telephone}`}>{telephone}</a>
      )
    },
    {
      title: "Type",
      dataIndex: "permanent",
      key: "permanent",
      render: (permanent) => (
        permanent ? <Tag color="success">Permanent</Tag> : <Tag color="warning">Temporaire</Tag>
      ),
      width: 150,
    },
    {
      title: "Statut",
      dataIndex: "enabled",
      key: "enabled",
      render: (enabled) => (
        enabled ? <Tag color="success">Actif</Tag> : <Tag color="error">Inactif</Tag>
      ),
      width: 150,
    },
    {
      title: "Supérieur hiérarchique",
      dataIndex: ["superieur"],
      key: "personnel_id",
      width: 250,
      render: (superieur) => superieur ? (
        <>
          {check('VIEW-PERSONNEL') && superieur ?
            (<NavLink to={`/personnel/${superieur.uid}`}>
              <Avatar src={superieur.avatar_link} /> &nbsp;
              {superieur.name}
            </NavLink>
            ) : superieur ?
              (
                <>
                  <Avatar src={superieur.avatar_link} /> &nbsp;
                  {superieur.name}
                </>
              )
              : <span className="badge bg-secondary">Aucun</span>}
        </>
      ) : <span className="badge bg-secondary">Aucun</span>
    },
    !searchParams?.departement_id && {
      title: "Departement Rattaché",
      dataIndex: ["departement", "nom"],
      key: "departement_id",
      width: 300,
      render: (departement, record) => (
        <>
          {check('VIEW-DEPARTEMENT') && departement ? (
            <NavLink to={`/departement/${record.departement.uid}`}>
              {departement}
            </NavLink>
          ) : departement ? <span>{departement}</span> : <span className="badge bg-secondary">Aucun</span>}
        </>
      )
    },
    {
      title: "Compte",
      dataIndex: "user",
      key: "user_id",
      render: (user) => user ? (
        <NavLink to={`/user/${user.uid}`}>
          <Avatar src={user.avatar} /> &nbsp;
          {user.name}
        </NavLink>
      ) : <span className="badge bg-secondary">Aucun</span>,
      width: 250,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                label: 'Voir',
                icon: <EyeFilled />,
                onClick: () => gotoPersonnelDetails(record)
              },
              {
                key: 'edit',
                label: 'Modifier',
                icon: <EditFilled />,
                onClick: () => openEditModal(record)
              },
              {
                key: 'toggle',
                label: record.enabled ? 'Désactiver' : 'Activer',
                icon: record.enabled ? <LockFilled /> : <UnlockFilled />,
                onClick: () => {
                  Modal.confirm({
                    title: `Voulez-vous ${record.enabled ? 'désactiver' : 'activer'} cet personnel?`,
                    okText: record.enabled ? 'Désactiver' : 'Activer',
                    cancelText: 'Annuler',
                    okType: record.enabled ? 'danger' : 'primary',
                    onOk: () => record.enabled ? handleDisable(record) : handleEnable(record)
                  });
                }
              },
              !record.user && {
                key: 'generate-account',
                label: 'Générer un compte',
                icon: <UserAddOutlined />,
                onClick: () => {
                  Modal.confirm({
                    title: `Voulez-vous vraiment générer un compte pour cet personnel ?`,
                    okText: 'Confirmer',
                    cancelText: 'Annuler',
                    okType: 'primary',
                    onOk: () => {
                      generateAccount(record);
                    }
                  });
                }
              },
              {
                key: 'delete',
                label: 'Supprimer',
                icon: <DeleteFilled />,
                danger: true,
                onClick: () => {
                  Modal.confirm({
                    title: `Voulez-vous vraiment procéder à la suppression ?`,
                    okText: 'Confirmer',
                    cancelText: 'Annuler',
                    okType: 'danger',
                    onOk: () => handleDelete(record.uid)
                  });
                }
              }
            ].filter(item => {
              if (item.key === 'view') return check('VIEW-PERSONNEL');
              if (item.key === 'edit') return check('EDIT-PERSONNEL');
              if (item.key === 'delete') return check('DELETE-PERSONNEL');
              if (item.key === 'generate-account') return check('ADD-USER');
              return true;
            })
          }}
        >
          <Button>
            Actions <DownOutlined />
          </Button>
        </Dropdown>
      ),
      width: 120,
      fixed: 'right'
    },

  ];

  columns = columns.filter(column => column);

  return (
    <>
      <Spin spinning={loading}>
        <InfiniteTable
          rowKey={"uid"}
          dataSource={personnels}
          paginationData={paginationData}
          loadMore={loadMore}
          loading={loadingMore}
          columns={columns}
        />
      </Spin>
      {selectedPersonnel && (
        <PersonnelEditForm
          visible={updateModalVisible}
          uid={selectedPersonnel.uid}
          onUpdate={handleUpdate}
          onCancel={() => setUpdateModalVisible(false)}
        />
      )}
    </>
  );
}
export default PersonnelList;

PersonnelList.propTypes = {
  paginationData: PropTypes.object.isRequired,
  searchParams: PropTypes.object,
  setPaginationData: PropTypes.func.isRequired,
  personnels: PropTypes.array.isRequired,
  setPersonnels: PropTypes.func.isRequired
};